class ShippingNotice extends HTMLElement {
    constructor() {
      super();
      this.initialize();
      this.setupCartListeners();
    }

    initialize() {
      this.promoMode = this.dataset.promoMode || 'shipping-only';
      this.milestones = this.parseMilestones();
      this.thresholds = this.milestones.map((milestone) => milestone.threshold);
      this.cartTotal = parseInt(this.dataset.cartTotal, 10);
      this.currency = this.dataset.currency;

      this.messageElement = this.querySelector('.shipping-notice__text');
      this.progressBars = this.querySelectorAll('.progress-bar');
      this.iconWrappers = this.querySelectorAll('.icon-wrapper');
      this.lastRenderedMessage = null;

      if (this.messageElement) {
        this.messageElement.classList.add('notranslate');
        this.messageElement.setAttribute('translate', 'no');
      }

      this.updateProgress();
    }

    setMessage(message) {
      if (!this.messageElement) {
        return;
      }

      if (this.lastRenderedMessage === message) {
        return;
      }

      this.lastRenderedMessage = message;
      this.messageElement.innerHTML = message;
    }

    parseMilestones() {
      if (this.dataset.milestones) {
        try {
          const milestones = JSON.parse(this.dataset.milestones)
            .map((milestone) => ({
              threshold: parseInt(milestone.threshold, 10),
              label: milestone.label,
              icon: milestone.icon,
              includesShipping: Boolean(milestone.includesShipping),
            }))
            .filter((milestone) => Number.isFinite(milestone.threshold));

          if (milestones.length) {
            return milestones.sort((left, right) => left.threshold - right.threshold);
          }
        } catch (error) {
          console.error('Error parsing milestone data:', error);
        }
      }

      const thresholds = [];

      if (this.dataset.firstGift) {
        thresholds.push({
          threshold: parseInt(this.dataset.firstGift, 10),
          label: window.theme?.strings?.free_gift_label || 'Dárek zdarma',
          icon: 'gift',
          includesShipping: false,
        });
      }

      if (this.dataset.freeShipping) {
        thresholds.push({
          threshold: parseInt(this.dataset.freeShipping, 10),
          label: window.theme?.strings?.free_shipping_label || 'Doprava zdarma',
          icon: 'truck',
          includesShipping: true,
        });
      }

      if (this.dataset.secondGift) {
        thresholds.push({
          threshold: parseInt(this.dataset.secondGift, 10),
          label: window.theme?.strings?.free_gift_label || 'Dárek zdarma',
          icon: 'gift',
          includesShipping: Boolean(this.dataset.freeShipping),
        });
      }

      return thresholds;
    }

    updateProgress() {
      const segments = this.calculateSegments();

      segments.forEach((segment, index) => {
        if (this.progressBars[index]) {
          this.progressBars[index].style.width = `${segment.progress}%`;
        }
        if (this.iconWrappers[index]) {
          this.iconWrappers[index].classList.toggle('active', segment.isComplete);
        }
      });

      this.updateTieredFinalState(segments);
      this.updateMessage(segments);
    }

    updateTieredFinalState(segments) {
      if (this.promoMode !== 'tiered-exclusive' || this.iconWrappers.length < 3) {
        return;
      }

      const finalTierWrapper = this.iconWrappers[this.iconWrappers.length - 1];
      const finalTierSegment = segments[segments.length - 1];

      if (finalTierWrapper && finalTierSegment) {
        finalTierWrapper.classList.toggle('icon-wrapper--show-shipping-bonus', finalTierSegment.includesShipping);
      }
    }

    calculateSegments() {
      return this.milestones.map((milestone, index) => {
        const start = index > 0 ? this.milestones[index - 1].threshold : 0;
        const segmentValue = milestone.threshold - start;
        const progress = Math.min(100, Math.max(0, ((this.cartTotal - start) / segmentValue) * 100));

        return {
          ...milestone,
          start,
          end: milestone.threshold,
          progress,
          isComplete: this.cartTotal >= milestone.threshold,
          remaining: Math.max(0, milestone.threshold - this.cartTotal),
        };
      });
    }

    updateMessage(segments) {
      if (this.promoMode === 'tiered-exclusive') {
        this.updateTieredMessage(segments);
        return;
      }

      this.updateLegacyMessage(segments);
    }

    updateLegacyMessage(segments) {
      if (this.thresholds.length === 1) {
        const segment = segments[0];
        const formattedRemaining = this.formatMoney(segment.remaining);

        if (segment.isComplete) {
          this.setMessage(
            window.theme?.strings?.free_shipping_alone_eligible_html || 'Máte nárok na dopravu zdarma!'
          );
        } else {
          let message =
            window.theme?.strings?.free_shipping_remaining_html ||
            'Objednejte ještě za {remaining_amount} a máte dopravu zdarma!';
          message = message.replace(/\{\{\s*remaining_amount\s*\}\}/g, formattedRemaining);
          message = message.replace('{remaining_amount}', formattedRemaining);
          this.setMessage(message);
        }
        return;
      }

      const activeSegment = segments.find((segment) => !segment.isComplete);
      const lastCompletedIndex = segments.findIndex((segment) => !segment.isComplete) - 1;

      if (!activeSegment) {
        this.setMessage(
          window.theme?.strings?.free_gifts_shipping_eligible || 'Máte nárok na dopravu zdarma a všechny dárky!'
        );
        return;
      }

      const formattedRemaining = this.formatMoney(activeSegment.remaining);
      const segmentIndex = segments.indexOf(activeSegment);

      let messages = [];

      if (lastCompletedIndex >= 0) {
        let achievementMessage;
        switch (lastCompletedIndex) {
          case 0:
            achievementMessage = window.theme?.strings?.free_gift_eligible;
            break;
          case 1:
            achievementMessage = window.theme?.strings?.free_shipping_eligible;
            break;
          case 2:
            achievementMessage = window.theme?.strings?.free_gift_eligible;
            break;
        }

        if (achievementMessage) {
          messages.push(achievementMessage);
        }
      }

      let progressMessage;
      switch (segmentIndex) {
        case 0:
          progressMessage =
            window.theme?.strings?.first_free_gift_remaining_html ||
            'Už jen {remaining_amount} a máte malé balení Mřížek zdarma!';
          break;
        case 1:
          progressMessage =
            window.theme?.strings?.free_shipping_remaining_html ||
            'Objednejte ještě za {remaining_amount} a máte dopravu zdarma!';
          break;
        case 2:
          progressMessage =
            window.theme?.strings?.second_free_gift_remaining_html ||
            'Už jen {remaining_amount} a máte 1 kg Krekrů zdarma!';
          break;
      }

      progressMessage = progressMessage.replace(/\{\{\s*remaining_amount\s*\}\}/g, formattedRemaining);
      progressMessage = progressMessage.replace('{remaining_amount}', formattedRemaining);
      messages.push(progressMessage);

      this.setMessage(messages.join('<br>'));
    }

    updateTieredMessage(segments) {
      if (!segments.length) {
        this.setMessage('');
        return;
      }

      const activeSegment = segments.find((segment) => !segment.isComplete);
      const lastCompletedSegment = [...segments].reverse().find((segment) => segment.isComplete);

      if (!activeSegment) {
        this.setMessage(
          this.interpolateMessage(
            window.theme?.strings?.tiered_reward_final_html ||
              'Máte <strong>{{ current_reward }}</strong>{{ shipping_text }}.',
            {
              current_reward: this.formatRewardName(segments[segments.length - 1].label),
              shipping_text: this.getShippingText(segments[segments.length - 1]),
            }
          )
        );
        return;
      }

      const formattedRemaining = this.formatMoney(activeSegment.remaining);

      if (!lastCompletedSegment) {
        this.setMessage(
          this.interpolateMessage(
            window.theme?.strings?.tiered_reward_remaining_html ||
              'Objednejte ještě za <strong>{{ remaining_amount }}</strong> a získáte <strong>{{ reward_name }}</strong>!',
            {
              remaining_amount: formattedRemaining,
              reward_name: this.formatRewardName(activeSegment.label),
            }
          )
        );
        return;
      }

      this.setMessage(
        this.interpolateMessage(
          window.theme?.strings?.tiered_reward_upgrade_html ||
            'Máte <strong>{{ current_reward }}</strong>{{ shipping_text }}. Už jen <strong>{{ remaining_amount }}</strong> a získáte <strong>{{ next_reward }}</strong>!',
          {
            current_reward: this.formatRewardName(lastCompletedSegment.label),
            next_reward: this.formatRewardName(activeSegment.label),
            remaining_amount: formattedRemaining,
            shipping_text: this.getShippingText(lastCompletedSegment),
          }
        )
      );
    }

    getShippingText(segment) {
      if (!segment?.includesShipping) {
        return '';
      }

      return window.theme?.strings?.tiered_reward_shipping_suffix_html || ' a <strong>dopravu zdarma</strong>';
    }

    interpolateMessage(template, replacements) {
      return Object.entries(replacements).reduce((message, [key, value]) => {
        const pattern = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}|\\{${key}\\}`, 'g');
        return message.replace(pattern, value);
      }, template);
    }

    formatRewardName(label) {
      return `<span class="notranslate">${this.escapeHtml(label)}</span>`;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    formatMoney(cents) {
      if (typeof window.Shopify?.formatMoney === 'function') {
        const moneyFormat =
          window.theme?.moneyFormat?.replace(/\{\{\s*amount.*?\s*\}\}/, '{{ amount_no_decimals }}') ||
          window.Shopify.money_format?.replace(/\{\{\s*amount.*?\s*\}\}/, '{{ amount_no_decimals }}') ||
          '{{ amount_no_decimals }}';
        return window.Shopify.formatMoney(cents, moneyFormat);
      }

      const localeMap = {
        CZK: 'cs-CZ',
        EUR: 'de-DE',
        PLN: 'pl-PL',
        CHF: 'de-CH',
        HUF: 'hu-HU',
        NOK: 'nb-NO',
        DKK: 'da-DK',
        SEK: 'sv-SE',
      };

      return new Intl.NumberFormat(localeMap[this.currency] || 'en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    }

    setupCartListeners() {
      const events = ['cart:updated', 'cart:refresh', 'cart:change', 'cart_update', 'cart.requestComplete'];

      events.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          let newTotal = null;

          if (event.detail?.cart?.total_price) {
            newTotal = event.detail.cart.total_price;
          } else if (event.detail?.total_price) {
            newTotal = event.detail.total_price;
          } else if (window.cart?.total_price) {
            newTotal = window.cart.total_price;
          }

          if (newTotal !== null) {
            this.cartTotal = parseInt(newTotal);
            this.updateProgress();
          }
        });
      });

      this.setupFetchListener();
    }

    setupFetchListener() {
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        const clone = response.clone();

        if (args[0].includes('/cart.js') || args[0].includes('/cart.json')) {
          try {
            const data = await clone.json();
            if (data.total_price !== undefined) {
              this.cartTotal = data.total_price;
              this.updateProgress();
            }
          } catch (e) {
            console.error('Error parsing cart data:', e);
          }
        }

        return response;
      };
    }
  }

  customElements.define('shipping-notice', ShippingNotice);
